import React from "react";

import { cn } from "@/lib/style";

interface PlaceCardOverlayProps {
  name: string
  rating?: number // 0.0-5.0
  priceLevel?: number // 1-4 ($-$$$$)
  variant: 'compact' | 'expanded' | 'timeline'
  className?: string
}

/**
 * Text overlay component for bottom-left title and rating on photo
 * Uses monochromatic color scheme with dark gradient
 */
export function PlaceCardOverlay({
  name,
  rating,
  priceLevel,
  variant,
  className,
}: PlaceCardOverlayProps) {
  // Format rating as stars
  const formatRating = (rating: number): string => {
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 >= 0.5
    let stars = '★'.repeat(fullStars)
    if (hasHalfStar) stars += '½'
    return stars
  }

  // Format price level as dollar signs
  const formatPriceLevel = (level: number): string => {
    return '$'.repeat(Math.max(1, Math.min(4, level)))
  }

  const isCompact = variant === 'compact'

  return (
    <div
      className={cn(
        'absolute bottom-0 left-0 right-0',
        isCompact ? 'p-1' : 'p-2',
        className
      )}
      style={{
        background: `linear-gradient(
          to top,
          rgba(0, 0, 0, 0.85) 0%,
          rgba(0, 0, 0, 0.6) 50%,
          rgba(0, 0, 0, 0) 100%
        )`,
      }}
    >
      {/* Place name - bottom left aligned */}
      <h3
        className={cn(
          'font-serif font-medium text-white leading-tight text-left',
          isCompact ? 'text-sm line-clamp-2 mb-0.3' : 'text-base line-clamp-2 mb-0.5',
        )}
        style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}
      >
        {name}
      </h3>

      {/* Rating and price - below title, left aligned, separated by dash */}
      {(rating !== undefined || priceLevel !== undefined) && (
        <div
          className="flex items-center gap-1 justify-start"
          style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)' }}
        >
          {rating !== undefined && (
            <span className={cn('text-white', isCompact ? 'text-xs' : 'text-sm')}>
              {formatRating(rating)}
            </span>
          )}
          {rating !== undefined && priceLevel !== undefined && (
            <span className="text-white/60">-</span>
          )}
          {priceLevel !== undefined && (
            <span
              className={cn(
                'text-white font-medium',
                isCompact ? 'text-xs' : 'text-sm'
              )}
            >
              {formatPriceLevel(priceLevel)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

interface PlaceCardMetadataProps {
  category?: string
  distance?: string
  savedDate?: Date
  savedFrom?: 'search' | 'map' | 'ai' | 'manual'
  userNote?: string
  variant: 'compact' | 'expanded' | 'timeline'
  className?: string
}

/**
 * Metadata section below photo (type, distance, description)
 * Displayed on white background below the image
 */
export function PlaceCardMetadata({
  category,
  distance,
  savedDate,
  savedFrom,
  userNote,
  variant,
  className,
}: PlaceCardMetadataProps) {
  // Format saved date as timeago
  const formatTimeAgo = (date: Date): string => {
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'today'
    if (diffDays === 1) return 'yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return `${Math.floor(diffDays / 30)} months ago`
  }

  // Format saved source
  const formatSource = (source: string): string => {
    const sourceLabels = {
      search: 'search',
      map: 'map',
      ai: 'AI chat',
      manual: 'manual',
    }
    return sourceLabels[source as keyof typeof sourceLabels] || source
  }

  const isExpanded = variant === 'expanded'

  // Don't render if no metadata to show
  if (!category && !distance && !savedDate && !userNote) {
    return null
  }

  // Determine if we have extended content (user notes, saved info, etc.)
  const hasExtendedContent = !!(userNote || savedDate)

  // Use tighter padding for simple search results without extended content
  // Use more generous padding for candidate cards with notes/actions
  const paddingClasses = hasExtendedContent
    ? 'px-2 pt-2 pb-1 space-y-1'  // Original padding for candidates with notes
    : 'px-2 pt-1.5 pb-1 space-y-0.5'  // Tighter for simple search results

  return (
    <div className={cn('bg-stone-100', paddingClasses, className)}>
      {/* Category on first line */}
      {category && (
        <div className="text-xs font-semibold text-magazine-charcoal text-left">
          {category}
        </div>
      )}

      {/* Address/distance on second line */}
      {distance && (
        <div className="text-xs font-semibold text-magazine-charcoal text-left truncate">
          {distance}
        </div>
      )}

      {/* Saved info (for candidates)
      {isExpanded && savedDate && (
        <div className="text-xs text-magazine-ash italic text-left">
          Saved {formatTimeAgo(savedDate)}
          {savedFrom && ` from ${formatSource(savedFrom)}`}
        </div>
      )} */}

      {/* User note / description */}
      {isExpanded && userNote && (
        <div className="text-xs text-magazine-charcoal line-clamp-2 text-left">
          {userNote}
        </div>
      )}
    </div>
  )
}
